import { BALANCE, createGuestName, type PlayerProfile } from '@2k48/shared';
import { api } from '../../net/client';
import { storage } from '../../platform/storage';
import { auth } from './auth.svelte';

/**
 * Who is playing.
 *
 * The identity is **chosen**, not assumed. Opening the app for the first time
 * lands on the sign-in screen, and the device gets a player only once somebody
 * presses JUGAR COMO INVITADO or signs in. That is the difference from the
 * previous behaviour, where a guest row was created on the server during boot
 * whether or not the person ever intended to play.
 *
 * Two consequences worth stating, because most of this file follows from them:
 *
 *  - `profile === null` is now a real, expected state — "nobody has chosen
 *    yet". It is not an error and not a failed request. `hasIdentity` is what
 *    the shell gates on.
 *  - The identity is **cached locally and granted before the network is asked**.
 *    A device that has played before is that player on the next cold start with
 *    no connection at all. The server is how the identity becomes visible to
 *    everyone else, not how the player gets a seat.
 *
 * Worth being explicit about what the *device id* is not: it is not
 * authentication. It is a claim, not a proof. That is acceptable precisely
 * because the client never reports its own score — the server writes scores
 * from matches it refereed, so the identity answers "who played", never "what
 * did they get".
 *
 * Signing in does not replace this store, it resolves it differently: an
 * account owns the very player row the guest was already using, so `profile`
 * keeps meaning the same thing either way. See [[auth.svelte]].
 */

const DEVICE_KEY = '2k48.deviceId';
/** Last known identity of this device, guest or account. The offline seat. */
const IDENTITY_KEY = '2k48.identity';
/** Present exactly when that identity is a guest, and holds its chosen name. */
const GUEST_NAME_KEY = '2k48.guest.name';

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

async function deviceId(): Promise<string> {
  const existing = await storage.get(DEVICE_KEY);
  if (existing) return existing;

  const created = crypto.randomUUID();
  await storage.set(DEVICE_KEY, created);
  return created;
}

/**
 * Uniform in [0, 1), from the platform CSPRNG.
 *
 * `createGuestName` lives in `shared/` and therefore cannot reach for
 * randomness itself; supplying it is this side's job. `Math.random()` would do
 * for a cosmetic name, but the CSPRNG is already here for `randomUUID` and
 * costs nothing.
 */
function secureUnitRandom(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return (buffer[0] as number) / 2 ** 32;
}

/** Cheap shape check: storage can hold anything a previous version wrote. */
function isProfile(value: unknown): value is PlayerProfile {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<PlayerProfile>;
  return typeof candidate.id === 'string' && typeof candidate.displayName === 'string';
}

class Session {
  profile = $state<PlayerProfile | null>(null);
  /**
   * False until `init()` has decided. The shell must not paint either the menu
   * or the sign-in screen before this flips, or a returning player sees a login
   * form flash on every cold start.
   *
   * It flips as soon as the *local* decision is made, because a request has no
   * deadline on a bad network and a boot screen that hangs is worse than one
   * that is briefly optimistic.
   *
   * One exception, bounded by `identityRestoreGraceMs`: a device holding a
   * session token but no cached identity waits for that token to be resolved,
   * because the only screen it could paint meanwhile is the wrong one.
   */
  ready = $state(false);
  /**
   * Current Versus rating, or null while unknown or never rated.
   *
   * Held here rather than fetched per screen because two of them show it and
   * the matchmaking queue *sends* it: a screen that displayed one band while
   * the join used another would be showing a rating the server is not matching
   * on. Null is a real state and renders as "open", never as a made-up 1000.
   */
  rating = $state<number | null>(null);
  /** Best solo score, for the footer of the modes menu. Null until known. */
  bestScore = $state<number | null>(null);
  /** Null while unknown, false once we have tried and failed. */
  online = $state<boolean | null>(null);
  error = $state<string | null>(null);
  /**
   * True when this device holds a guest the server has not confirmed — created
   * offline, or created while the server was down. `syncGuest()` clears it.
   */
  pendingSync = $state(false);
  /**
   * This device's identifier, resolved during `init`. Registration needs it:
   * it is what tells the server which guest is being claimed, and it is
   * deliberately not the player id, which every leaderboard publishes.
   */
  deviceId = $state<string | null>(null);

  /** Has somebody chosen an identity on this device? Gates the whole shell. */
  get hasIdentity(): boolean {
    return this.profile !== null;
  }

  /** A guest whose row the server has never acknowledged. Shown, never hidden. */
  get isProvisional(): boolean {
    return this.hasIdentity && this.profile?.id === '';
  }

  /**
   * Resolve the identity this device already has, if any.
   *
   * Reads storage, decides, and returns. Whatever revalidation is worth doing
   * happens afterwards without anybody waiting for it — see `revalidate()`.
   *
   * Being offline is never "no identity". A player who has been here before
   * stays themselves with no connection at all; what they lose is the server's
   * copy of their profile, not their seat.
   */
  async init(): Promise<void> {
    /**
     * A token but no cached identity: the identity exists, it just has not been
     * read back yet.
     *
     * The case is real and arrives all at once — every signed-in player has it
     * on their first launch after `2k48.identity` was introduced, because
     * nothing wrote that key before it existed. Painting from an empty cache
     * would hand all of them the sign-in screen for the length of one round
     * trip and then yank them to the menu, which reads as being signed out
     * rather than as loading.
     */
    let awaitingAccount = false;

    try {
      this.deviceId = await deviceId();

      const cached = await storage.getJson<unknown>(IDENTITY_KEY, null);
      if (isProfile(cached)) {
        this.profile = cached;
        this.pendingSync = cached.id === '';
      } else {
        awaitingAccount = await auth.hasStoredToken();
      }
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      // The shell can paint from here. Everything below is a correction.
      if (!awaitingAccount) this.ready = true;
    }

    const settled = this.revalidate();
    if (!awaitingAccount) {
      void settled;
      return;
    }

    // Bounded, because the reason to wait is to avoid a wrong screen, not to
    // avoid a screen. A dead network still has to land somewhere, and landing
    // on sign-in after a beat is the honest answer when the session cannot be
    // confirmed. `adopt()` flips `ready` the moment it succeeds, so the common
    // case never spends the whole grace.
    await Promise.race([settled, wait(BALANCE.account.identityRestoreGraceMs)]);
    this.ready = true;
  }

  /**
   * JUGAR COMO INVITADO.
   *
   * The name is minted here, on the device, before anything is sent. That
   * ordering is what makes a first launch with no connection work: the player
   * gets a name and a seat immediately, and the server learns about both from
   * `syncGuest()`, which upserts on the device id and therefore returns the
   * same row however many times it runs.
   */
  async playAsGuest(): Promise<void> {
    this.deviceId = await deviceId();

    // Reuse the name if this device already had one: pressing the button twice,
    // or pressing it after signing out of an account, must not rename a guest
    // whose old name is already on a leaderboard row.
    const existing = await storage.get(GUEST_NAME_KEY);
    const name = existing ?? createGuestName(secureUnitRandom);
    if (!existing) await storage.set(GUEST_NAME_KEY, name);

    const now = Date.now();
    await this.remember({
      id: '',
      displayName: name,
      avatarId: 0,
      createdAt: now,
      lastSeenAt: now,
    });
    this.pendingSync = true;
    this.ready = true;

    await this.syncGuest();
  }

  /**
   * Adopt the identity an account resolved to. Called after sign-in and after
   * registration, which return the profile in the same response — so the menu
   * is correct on the next frame rather than after another round trip.
   */
  adopt(profile: PlayerProfile | null): void {
    if (!profile) return;
    this.online = true;
    this.error = null;
    this.pendingSync = false;
    this.ready = true;
    void this.remember(profile);
    void this.loadRating();
  }

  /**
   * Hand the guest this device decided on to the server, and take back the row
   * it owns.
   *
   * Safe to call whenever: `POST /profile/guest` upserts on the device id, so a
   * guest created offline three days ago and one created two seconds ago both
   * resolve to exactly one player. Failing leaves the local identity untouched
   * — that is the point of it being local.
   */
  async syncGuest(): Promise<void> {
    const name = await storage.get(GUEST_NAME_KEY);
    if (!name || !this.deviceId || auth.signedIn) return;

    try {
      const profile = await api<PlayerProfile>('/profile/guest', {
        method: 'POST',
        body: JSON.stringify({ deviceId: this.deviceId, displayName: name }),
      });
      await this.remember(profile);
      this.online = true;
      this.error = null;
      this.pendingSync = false;
      void this.loadRating();
    } catch (error) {
      this.online = false;
      this.error = error instanceof Error ? error.message : String(error);
      this.pendingSync = this.profile?.id === '';
    }
  }

  /**
   * Sign out, all the way back to the sign-in screen.
   *
   * The device id is rotated, and that is the whole point rather than a detail:
   * an account claimed the player this device resolved to, so keeping the old
   * id would hand that same player straight back as a guest — "cerrar sesión"
   * would sign nobody out, and on a shared phone the next person would inherit
   * the previous player's history.
   *
   * The cached identity and the guest name go with it, which is what leaves the
   * device with nothing and sends the shell back to sign-in rather than into a
   * freshly minted guest nobody asked for.
   */
  async signOut(): Promise<void> {
    await storage.set(DEVICE_KEY, crypto.randomUUID());
    await storage.remove(IDENTITY_KEY);
    await storage.remove(GUEST_NAME_KEY);

    this.deviceId = await deviceId();
    this.profile = null;
    this.rating = null;
    this.bestScore = null;
    this.pendingSync = false;
    this.online = null;
    this.error = null;
  }

  /**
   * Rename, with proof that the row is ours. A signed-in player is covered by
   * the bearer token `api()` already attaches; a guest sends the device id,
   * which is the only thing that distinguishes them from a stranger who read
   * their player id off a leaderboard.
   */
  async rename(displayName: string, avatarId?: number): Promise<void> {
    if (!this.profile) return;

    // A guest the server has never seen has no row to rename yet. Keep the
    // chosen name locally; the next sync carries it up.
    if (!this.profile.id) {
      await storage.set(GUEST_NAME_KEY, displayName);
      await this.remember({
        ...this.profile,
        displayName,
        avatarId: avatarId ?? this.profile.avatarId,
      });
      return;
    }

    const updated = await api<PlayerProfile>(`/profile/${this.profile.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ displayName, avatarId, deviceId: this.deviceId }),
    });
    await this.remember(updated);
    if (!auth.signedIn) await storage.set(GUEST_NAME_KEY, updated.displayName);
  }

  // --- internals -------------------------------------------------------------

  /**
   * Bring the cached identity up to date, in the background.
   *
   * A live session wins over the guest lookup and is tried first, so a
   * signed-in player never sees their old guest name flash before their own.
   * `auth.restore()` resolves to null for every failure — expired, revoked,
   * offline — and the guest path is the answer to all of them.
   */
  private async revalidate(): Promise<void> {
    try {
      // Asked *before* restoring, because restoring is what can clear it.
      const tokenOnDevice = await auth.hasStoredToken();

      const restored = await auth.restore();
      if (restored) {
        this.adopt(restored);
        return;
      }

      if (await storage.get(GUEST_NAME_KEY)) {
        await this.syncGuest();
        return;
      }

      // Neither an account we could confirm nor a guest name. A cached identity
      // is still on screen, and the question is whether it is stale.
      //
      // A token still sitting in storage means `restore()` failed for a reason
      // it refuses to distinguish — expired, revoked, or simply no network —
      // and signing the player out over a lift ride would be the worst of the
      // three readings. Keep the seat; the next launch with a connection
      // settles it. Only an absent token proves the identity is really gone.
      if (tokenOnDevice) {
        this.online = false;
        return;
      }
      if (this.profile) await this.signOut();
    } catch (error) {
      this.online = false;
      this.error = error instanceof Error ? error.message : String(error);
    }
  }

  /** One place writes `profile`, so the cache can never drift from the state. */
  private async remember(profile: PlayerProfile): Promise<void> {
    this.profile = profile;
    await storage.setJson(IDENTITY_KEY, profile);
  }

  /**
   * Best-effort: a missing rating only costs a narrower label, so it must never
   * be able to stop a player reaching the queue.
   */
  private async loadRating(): Promise<void> {
    const id = this.profile?.id;
    if (!id) return;
    try {
      const response = await api<{
        stats?: { versusRating?: number | null; bestScore?: number | null };
      }>(`/profile/${id}`);
      this.rating = finiteOrNull(response.stats?.versusRating);
      this.bestScore = finiteOrNull(response.stats?.bestScore);
    } catch {
      this.rating = null;
      this.bestScore = null;
    }
  }
}

export const session = new Session();
